import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  QrCode as QrCodeIcon,
  Email as EmailIcon,
  VpnKey as KeyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useMFA } from '../../contexts/MFAContext';
import { MFATypes } from '@flashbacktech/flashbackclient/api';
import GoogleAuthSetup from './GoogleAuthSetup';
import MagicLinkSetup from './MagicLinkSetup';
import PasskeySetup from './PasskeySetup';

const MFASetup: React.FC = () => {
  const {
    mfaState,
    enableMFAMethod,
    disableMFAMethod,
    setPrimaryMethod,
    setupMFAMethod,
    clearError,
    fetchMFAStatus,
    fetchAvailableMethods,
  } = useMFA();
  const [selectedMethod, setSelectedMethod] = useState<MFATypes.MFAType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSetupMethod = (mfaType: MFATypes.MFAType) => {
    setupMFAMethod(mfaType);
    setSelectedMethod(mfaType);
  };

  const handleSetupComplete = () => {
    setSelectedMethod(null);

    // Force a refresh of MFA data to show the updated status
    // This will be handled by the MFAContext after verification
  };

  const handleRefreshMFA = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchMFAStatus(), fetchAvailableMethods()]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableMethod = async (mfaType: MFATypes.MFAType) => {
    setIsLoading(true);
    try {
      await enableMFAMethod(mfaType);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableMethod = async (mfaType: MFATypes.MFAType) => {
    setIsLoading(true);
    try {
      await disableMFAMethod(mfaType);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPrimary = async (mfaType: MFATypes.MFAType) => {
    setIsLoading(true);
    try {
      await setPrimaryMethod(mfaType);
    } finally {
      setIsLoading(false);
    }
  };

  const getMethodIcon = (mfaType: MFATypes.MFAType) => {
    switch (mfaType) {
      case MFATypes.MFAType.GOOGLE_AUTH:
        return <QrCodeIcon />;
      case MFATypes.MFAType.MAGIC_LINK:
        return <EmailIcon />;
      case MFATypes.MFAType.PASSKEY:
        return <KeyIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  const getMethodName = (mfaType: MFATypes.MFAType) => {
    switch (mfaType) {
      case MFATypes.MFAType.GOOGLE_AUTH:
        return 'Google/MS Authenticator';
      case MFATypes.MFAType.MAGIC_LINK:
        return 'Magic Link';
      case MFATypes.MFAType.PASSKEY:
        return 'Passkey';
      default:
        return 'Unknown Method';
    }
  };

  const renderSetupComponent = () => {
    if (!selectedMethod) return null;

    switch (selectedMethod) {
      case MFATypes.MFAType.GOOGLE_AUTH:
        return <GoogleAuthSetup onSetupComplete={handleSetupComplete} />;
      case MFATypes.MFAType.MAGIC_LINK:
        return <MagicLinkSetup onSetupComplete={handleSetupComplete} />;
      case MFATypes.MFAType.PASSKEY:
        return <PasskeySetup onSetupComplete={handleSetupComplete} />;
      default:
        return null;
    }
  };

  const methods = mfaState.methods;
  const status = mfaState.status;
  const error = mfaState.error;

  if (selectedMethod) {
    return (
      <Box>
        <Button variant="text" onClick={() => setSelectedMethod(null)} sx={{ mb: 2 }}>
          ← Back to MFA Methods
        </Button>
        {renderSetupComponent()}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={3}>
          <SecurityIcon color="primary" sx={{ fontSize: 32, mr: 2 }} />
          <Box>
            <Typography variant="h5" gutterBottom>
              Multi-Factor Authentication
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Add an extra layer of security to your account
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => clearError()}>
            {error}
          </Alert>
        )}

        {/* MFA Status */}
        {status && (
          <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Current Status
              </Typography>
              <Tooltip title="Refresh MFA Status">
                <IconButton onClick={handleRefreshMFA} size="small" color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                label={`MFA ${status.isEnabled ? 'Enabled' : 'Disabled'}`}
                color={status.isEnabled ? 'success' : 'default'}
                variant="outlined"
              />
              {status.isRequired && <Chip label="Required" color="warning" variant="outlined" />}
              {status.isEnforced && (
                <Chip label="Organization Enforced" color="error" variant="outlined" />
              )}
              {status.primaryMethod && (
                <Chip
                  label={`Primary: ${getMethodName(status.primaryMethod)}`}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}

        {/* Loading State */}
        {mfaState.isLoading && (
          <Box mb={3} p={2} bgcolor="blue.50" borderRadius={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="primary">
                Refreshing MFA status...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Available Methods */}
        <Typography variant="h6" gutterBottom>
          Available Methods
        </Typography>

        <Grid container spacing={3}>
          {methods.map((method) => (
            <Grid item xs={12} md={6} lg={4} key={method.type}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box color="primary.main" mr={1}>
                      {getMethodIcon(method.type)}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {method.name}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    {method.description}
                  </Typography>

                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    {method.isAvailable && <Chip label="Available" color="success" size="small" />}
                    {method.isConfigured && <Chip label="Configured" color="info" size="small" />}
                    {method.isEnabled && <Chip label="Active" color="success" size="small" />}
                    {status?.primaryMethod === method.type && (
                      <Chip label="Primary" color="primary" size="small" />
                    )}
                  </Box>

                  <Box display="flex" gap={1} flexWrap="wrap">
                    {!method.isConfigured && method.isAvailable && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSetupMethod(method.type)}
                        disabled={isLoading}
                      >
                        Setup
                      </Button>
                    )}

                    {method.isConfigured && !method.isEnabled && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEnableMethod(method.type)}
                        disabled={isLoading}
                      >
                        Enable
                      </Button>
                    )}

                    {method.isEnabled && (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleDisableMethod(method.type)}
                          disabled={isLoading}
                        >
                          Disable
                        </Button>
                        {status?.primaryMethod !== method.type && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleSetPrimary(method.type)}
                            disabled={isLoading}
                          >
                            Set Primary
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {methods.length === 0 && (
          <Box textAlign="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary" mt={2}>
              Loading available MFA methods...
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Security Tips */}
        <Box p={2} bgcolor="blue.50" borderRadius={1}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Security Tips
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Enable at least one MFA method for enhanced security
            <br />
            • Use backup codes as a fallback option
            <br />
            • Consider setting up multiple methods for redundancy
            <br />• Keep your backup codes in a secure location
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MFASetup;
